import React, { useState } from 'react';
import { Button } from './ui/button';
import { Play, Brain, Code, Rocket, TrendingUp, Clock, ChevronRight } from 'lucide-react';
import { previewModules } from '../data/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const iconMap = {
  Brain: Brain,
  Code: Code,
  Rocket: Rocket,
  TrendingUp: TrendingUp
};

const PreviewSection = () => {
  const [activeModule, setActiveModule] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.1 });

  const handleModuleClick = (moduleId) => {
    setActiveModule(activeModule === moduleId ? null : moduleId);
  };

  return (
    <section id="preview" className="preview-section" data-testid="preview-section">
      <div className="container">
        <div 
          ref={headerRef}
          className={`section-header animate-slide-up ${headerVisible ? 'is-visible' : ''}`}
        >
          <span className="section-tag">Interactive Preview</span>
          <h2 className="section-title">Experience the Course</h2>
          <p className="section-subtitle">
            Get a taste of what you'll learn with free preview content
          </p>
        </div>

        <div ref={contentRef} className="preview-content">
          {/* Video Preview */}
          <div className={`video-preview-container animate-scale ${contentVisible ? 'is-visible' : ''}`}>
            <div className="video-wrapper">
              {!isVideoPlaying ? (
                <div className="video-placeholder">
                  <div className="video-overlay">
                    <button 
                      className="play-button hover-glow"
                      onClick={() => setIsVideoPlaying(true)}
                      aria-label="Play course preview"
                      data-testid="play-preview-btn"
                    >
                      <Play className="play-icon" />
                    </button>
                    <p className="video-label">Course Introduction</p>
                    <span className="video-duration">
                      <Clock size={14} />
                      5 min
                    </span>
                  </div>
                  <div className="video-gradient"></div>
                </div>
              ) : (
                <div className="video-playing">
                  <div className="video-simulation">
                    <Brain className="simulation-icon" />
                    <p>Video Preview Playing...</p>
                    <span className="simulation-note">Full video available after enrollment</span>
                    <Button 
                      className="btn-secondary hover-lift"
                      onClick={() => setIsVideoPlaying(false)}
                    >
                      Close Preview
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Module Cards */}
          <div className={`module-cards animate-slide-right ${contentVisible ? 'is-visible' : ''}`}>
            <h3 className="modules-title">Explore Modules</h3>
            {previewModules.map((module, index) => {
              const IconComponent = iconMap[module.icon];
              const isActive = activeModule === module.id;
              
              return (
                <div 
                  key={module.id}
                  className={`module-card card-animated ${isActive ? 'active' : ''}`}
                  onClick={() => handleModuleClick(module.id)}
                  style={{ transitionDelay: `${index * 100}ms` }}
                  data-testid={`module-card-${module.id}`}
                >
                  <div className="module-icon-wrapper">
                    <IconComponent className="module-icon" />
                  </div>
                  <div className="module-info">
                    <h4 className="module-title">{module.title}</h4>
                    <p className="module-description">{module.description}</p>
                    <span className="module-duration">
                      <Clock size={12} />
                      {module.duration}
                    </span>
                  </div>
                  <ChevronRight className={`module-arrow ${isActive ? 'rotated' : ''}`} />
                  
                  {isActive && (
                    <div className="module-expanded">
                      <p className="expanded-text">
                        This preview module gives you hands-on experience with the concepts covered in our full course.
                      </p>
                      <Button className="btn-primary module-cta hover-glow">
                        Start Preview
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PreviewSection;
